/**
 * CustomDocumentos.tsx — a prova (migration 090).
 *
 * Dele, 04/09/2026: *"aqui a gente está falando tudo que fez, mas não está
 * botando a prova, é só fala. De repente a gente coloca algo, seja um PDF, um
 * print, uma mensagem"*.
 *
 * ⚠️ **SÃO ANEXOS, NO PLURAL.** O campo aceita vários arquivos de uma vez —
 * *"você tem três notas para anexar, você anexa em UMA atividade só; não vai
 * criar três atividades, uma para cada documento"*. Cada arquivo vira uma
 * linha; a atividade continua sendo uma.
 *
 * ⚠️ **Imagem o app comprime; PDF, não.** A compressão do cliente (1600 px /
 * q 0.75) é a mesma da OS e do chat. PDF não se comprime no navegador de forma
 * confiável, então o limite de 10 MB é o que existe — e a mensagem de recusa
 * diz isso, em vez de só falhar.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import Lightbox from 'yet-another-react-lightbox';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';

export interface Documento {
  id: number;
  autorizacao_id: number;
  etapa_id: number | null;
  etapa_titulo: string | null;
  atividade_id: number | null;
  tipo: string;
  url: string;
  nome: string | null;
  mime: string;
  tamanho_bytes: number;
  descricao: string | null;
  /** O relato do registro a que este documento pertence — a sua explicação. */
  atividade_relato?: string | null;
  criacao: string | null;
  autor_id: number;
  autor_nome: string;
  e_imagem: boolean;
}

function tamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

interface Props {
  autorizacaoId: number;
  /** Com etapa, a lista é só a dela — e o que subir entra por ela. */
  etapaId?: number;
  podeEscrever?: boolean;
}

export function CustomDocumentos({ autorizacaoId, etapaId, podeEscrever }: Props) {
  const [linhas, setLinhas] = useState<Documento[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [lightbox, setLightbox] = useState<string | null>(null);
  /* ⛔ O DOCUMENTO TEM DE DIZER O QUE É. Dele, 05/09: *"ele anexa, não pergunta
     o que quer. Eu coloco um documento ali e não sei que documento é esse"*.
     Anexar dentro de um REGISTRO é o caminho principal — lá o relato explica.
     Aqui, no anexo avulso, a explicação é obrigatória.

     ⚠️ **MAS O BLOQUEIO MUDOU DE LUGAR em 11/09.** Antes o "Escolher arquivo"
     nascia desabilitado até haver descrição, e ele bateu nisso em produção:
     *"escolher arquivo está desabilitado, não sei por quê"* — e depois,
     reconhecendo a própria regra: *"concordo, só que não é intuitivo; eu estou
     acostumado a mexer na internet e não percebi essa sutileza"*.

     Uma trava muda na PORTA DE ENTRADA parece defeito. Agora escolhe-se o
     arquivo primeiro, vê-se o que foi escolhido, e o que espera a descrição é o
     ENVIO — onde o botão pode dizer o que falta. A regra sobrevive inteira;
     o que mudou é ela ter deixado de ser silenciosa. */

  const caminho = etapaId
    ? `/autorizacoes/etapas/${etapaId}/documentos`
    : `/autorizacoes/${autorizacaoId}/documentos`;

  const carregar = useCallback(async () => {
    try {
      setLinhas(await api.get<Documento[]>(caminho));
    } catch {
      /* a lista vazia já diz o que precisa */
    } finally {
      setCarregando(false);
    }
  }, [caminho]);

  useEffect(() => { carregar(); }, [carregar]);


  async function excluir(d: Documento) {
    try {
      await api.delete(`/autorizacoes/documentos/${d.id}`);
      carregar();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não deu para tirar.');
    }
  }

  if (carregando) {
    return (
      <div className="d-flex align-items-center gap-2 text-secondary small py-2">
        <span className="spinner-border spinner-border-sm" /> Carregando…
      </div>
    );
  }

  return (
    <div>
      {/* ⛔ AQUI NÃO SE ANEXA — decisão dele em 12/09/2026, ao usar:
          *"documentos eu vejo mais como uma coisa de consulta do que uma coisa
          de registro. A atividade deve ser registrada e a atividade deveria
          permitir incluir um documento."*

          Havia DOIS lugares de anexar, e o daqui era o pior: escolher o arquivo
          só o punha numa lista, e o envio era um SEGUNDO botão, que aparecia
          embaixo e só acendia depois da descrição. Nada dizia que o arquivo
          estava apenas ESCOLHIDO — *"eu não sei se o arquivo acabou anexado ou
          não. Eu não tenho ideia"*. E o dele, de fato, nunca saiu da tela.

          O segundo lugar não some por elegância: some porque o documento
          precisa do relato que diz o que ele é. Anexar dentro do registro
          resolve as duas coisas de uma vez. Esta aba fica sendo a COLEÇÃO. */}
      {podeEscrever && (
        <p className="text-secondary small">
          <i className="bi bi-paperclip me-1"></i>
          Para anexar, vá em <strong>Atividades</strong> e registre o que
          aconteceu — o documento entra junto do relato que diz o que ele é.
        </p>
      )}

      {linhas.length === 0 ? (
        <p className="text-secondary small mb-0">
          Nenhum documento ainda. A nota, o e-mail do fornecedor ou o print da
          conversa valem mais aqui do que na memória de quem participou.
        </p>
      ) : (
        <div className="row g-3">
          {linhas.map((d) => (
            <div className="col-12 col-md-6 col-xl-4" key={d.id}>
              <div className="card adminuiux-card h-100">
                <div className="card-body d-flex gap-3">
                  {d.e_imagem ? (
                    <img
                      src={d.url}
                      alt={d.nome ?? 'documento'}
                      className="rounded border object-fit-cover flex-shrink-0"
                      role="button"
                      style={{ width: '4rem', height: '4rem' }}
                      onClick={() => setLightbox(d.url)}
                    />
                  ) : (
                    <a href={d.url} target="_blank" rel="noreferrer"
                       className="avatar avatar-60 rounded bg-theme-1-subtle text-theme-1 flex-shrink-0">
                      <i className="bi bi-file-earmark-pdf h4 mb-0"></i>
                    </a>
                  )}
                  <div className="flex-grow-1 overflow-hidden">
                    <p className="mb-1 text-truncate" title={d.nome ?? undefined}>
                      <a href={d.url} target="_blank" rel="noreferrer" className="style-none">
                        {d.nome ?? 'documento'}
                      </a>
                    </p>
                    {/* A explicação: a que se digitou, ou o relato do registro
                        onde ele entrou. Um documento sem nenhuma das duas é
                        anterior a 05/09 — e diz isso, em vez de ficar mudo. */}
                    {d.descricao ? (
                      <p className="small mb-1">{d.descricao}</p>
                    ) : d.atividade_relato ? (
                      <p className="small mb-1 fst-italic text-secondary">
                        <i className="bi bi-chat-left-text me-1"></i>{d.atividade_relato}
                      </p>
                    ) : (
                      <p className="small mb-1 text-secondary">Sem descrição.</p>
                    )}
                    <p className="small text-secondary mb-0">
                      {d.autor_nome}
                      {d.criacao && <> · {formatarData(d.criacao.slice(0, 10))}</>}
                      {' · '}{tamanho(d.tamanho_bytes)}
                    </p>
                    {/* Na coleção do pedido, cada um diz de qual etapa veio. */}
                    {!etapaId && d.etapa_titulo && (
                      <p className="small mb-0 mt-1">
                        <span className="badge casaa-selo-neutro me-2">Etapa</span>
                        <span className="fw-medium">{d.etapa_titulo}</span>
                      </p>
                    )}
                  </div>
                  {podeEscrever && !d.atividade_id && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger p-0 align-self-start"
                      title="Tirar este documento"
                      onClick={() => excluir(d)}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Lightbox
        open={lightbox !== null}
        close={() => setLightbox(null)}
        slides={lightbox ? [{ src: lightbox }] : []}
      />
    </div>
  );
}
